/**
 * QuickBooks connection pill for the Revenue header. Shows a
 * "Connect QuickBooks" button until the one-time OAuth consent is
 * done, then a green status pill with the connected company name.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Link2, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const QuickBooksConnect = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () =>
    axios
      .get(`${API}/api/quickbooks/status`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));

  useEffect(() => {
    load();
  }, []);

  // One-shot toast after the OAuth round-trip (?qb=connected|error)
  useEffect(() => {
    const qb = searchParams.get("qb");
    if (!qb) return;
    if (qb === "connected") toast.success("QuickBooks connected.");
    if (qb === "error") toast.error("QuickBooks connection failed - try again.");
    const next = new URLSearchParams(searchParams);
    next.delete("qb");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/api/quickbooks/auth-url`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Couldn't start the QuickBooks connection.",
      );
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks?")) return;
    await axios.post(`${API}/api/quickbooks/disconnect`);
    toast.success("QuickBooks disconnected.");
    load();
  };

  if (!status || !status.configured) return null;

  if (!status.connected) {
    return (
      <button
        type="button"
        onClick={connect}
        disabled={busy}
        data-testid="qb-connect-btn"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2CA01C] border border-[#2CA01C]/40 hover:bg-[#2CA01C]/5 rounded-full px-3.5 py-1.5 transition-colors disabled:opacity-60"
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Link2 className="w-3.5 h-3.5" />
        )}
        Connect QuickBooks
      </button>
    );
  }

  return (
    <span
      data-testid="qb-status-pill"
      className="inline-flex items-center gap-1.5 text-xs text-stone-600 border border-stone-200 bg-white rounded-full px-3 py-1.5"
    >
      <span className="w-2 h-2 rounded-full bg-emerald-500" />
      QuickBooks
      {status.company_name ? ` · ${status.company_name}` : ""}
      {status.environment === "sandbox" && (
        <span className="text-[9px] font-mono uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-px">
          sandbox
        </span>
      )}
      <button
        type="button"
        onClick={disconnect}
        data-testid="qb-disconnect-btn"
        aria-label="Disconnect QuickBooks"
        className="text-stone-300 hover:text-rose-500 transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
};
